/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useEffect, useState } from 'react'
import config from '../../../config.json'
import { botURL, clinicTypes, Origin, routes, Variant } from '../../../consts'
import { useSelector, useDispatch } from 'react-redux'
import Actions from '../../../actions'
import { getLocale, $t } from '../../../i18n'
import { uriEncodeSearchParams } from '../../../utils/encodingUtils'
import moment from 'moment'
import _ from 'lodash'
import { eventTypes } from '../../../consts/eventConsts'
import {
  customClickTracking,
  fortmatBotAnalyticsData,
} from '../../../utils/eventUtils'
import { Button, Spin } from 'antd'
import { isProduction } from '../../../utils/envUtils'
import {
  getOriginAndVariantByClinic,
  isReferredToClinic,
} from '../../../utils/clinicsUtils'
import { getBotTypesByIntent, getIsHmoMember } from '../../../utils/hmoUtils'
import { isTaskCompleted } from '../../../utils/onBoardingUtils'
import { COMPLETED_HEALTH_HISTORY } from '../../../consts/OnBoardingConsts'
import { isMemberMinorChild, isMinor } from '../../../utils/familyUtils'

import './Bot.css'

const Bot = ({ onNext, outsideOrigin, outsideVariant }) => {
  const dispatch = useDispatch()
  const { resetIdleTimer } = useSelector(state => state.app)
  const userDetails = useSelector(state => state.profile?.userDetails)
  const otherMember = useSelector(state => state.account?.selectedMember)
  const {
    selectedService,
    isHTNFollowUp,
    isDMFollowUp,
    isMHFollowUp,
    hmoSelectedIntent,
  } = useSelector(state => state.getCare)
  const patientReferredToHtn = useSelector(state =>
    isReferredToClinic(state, clinicTypes.HTN)
  )

  const patientTasks = userDetails?.hmo_data?.on_boarding_tasks
  // if the patient has already answered the risk assesment bot
  // in case the patient did not completed this task, we should show this bot before primary bot
  const isCompleteHealthHistoryForm = isTaskCompleted(
    patientTasks,
    COMPLETED_HEALTH_HISTORY.name
  )

  const [loader, setLoader] = useState(true)
  const [title, setTitle] = useState(null)
  const [member, setMember] = useState(null)
  const [origin, setOrigin] = useState(Origin.PRIMARY)
  const [variant, setVariant] = useState(Variant.PRIMARY)

  const isHmoMember = getIsHmoMember(userDetails)

  // minor child should not see risk assesment bot
  const isMinor = isMemberMinorChild(member || userDetails)

  useEffect(() => {
    window.addEventListener('message', onEnd)

    return () => window.removeEventListener('message', onEnd)
  }, [variant, origin])

  useEffect(() => {
    if (otherMember) {
      setMember(otherMember)
      if (otherMember.id !== userDetails.id) {
        setTitle(`Answer as ${otherMember.firstName}`)
      }
    } else {
      setMember(userDetails)
    }
  }, [])

  useEffect(() => {
    if (outsideOrigin && outsideVariant) {
      setOrigin(outsideOrigin)
      setVariant(outsideVariant)
    } else {
      const isFollowUp = isHTNFollowUp || isDMFollowUp || isMHFollowUp
      const botInfo = getOriginAndVariantByClinic(
        selectedService,
        isFollowUp,
        isMinor
      )
      setOrigin(botInfo.origin)
      setVariant(botInfo.variant)
    }
  }, [
    selectedService,
    isHTNFollowUp,
    isDMFollowUp,
    isMHFollowUp,
    outsideOrigin,
    outsideVariant,
    isMinor,
    isHmoMember,
  ])

  const onBotEnded = () => {
    if (
      origin === Origin.RISK_ASSESSMENT &&
      variant === Variant.RISK_ASSESSMENT
    ) {
      dispatch(Actions.createAction(Actions.RISK_ASSESMENT_BOT_ENDED))
    } else {
      dispatch(Actions.createAction(Actions.BOT_WIZARD_ENDED))
    }

    // if we do have on next action, go to the next action
    // else, go to schedule flow - default
    if (onNext) {
      onNext()
    } else {
      dispatch(
        Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.GET_CARE)
      )
    }
  }

  const onExitBot = () => {
    if (origin === Origin.MENTAL || origin === Origin.MENTAL_FU) {
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          eventName: 'mh_bot_ended_web',
        })
      )
    }
    dispatch(Actions.createAction(Actions.BOT_WIZARD_CLOSED))
  }

  const onEnd = event => {
    const data = event?.data

    switch (data) {
      case 'init': {
        // is after concent false
        dispatch(
          Actions.createAction(Actions.BOT_CONCENT_STATUS_CHANGED, false)
        )
        break
      }
      case 'start': {
        // is after concent true
        dispatch(Actions.createAction(Actions.BOT_CONCENT_STATUS_CHANGED, true))
        break
      }
      case 'close': {
        onExitBot()

        break
      }
      case 'done':
        if (variant === Variant.MENTAL_FU) {
          dispatch(
            Actions.createAction(
              Actions.TRIGGER_NEXT_MH_FOLLOW_UP_BOT_REQUESTED
            )
          )
        }

        dispatch(
          Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            eventName: `${variant}_bot_ended`,
          })
        )

        onBotEnded()

        break
      case 'htn-talk-with-doctor':
        dispatch(Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.ROOT))
        break
      case 'page-loaded':
        const token = localStorage.getItem('@token')
        try {
          const parsedToken = JSON.parse(token)
          event.source.postMessage(
            { type: 'take_my_token', token: parsedToken.token },
            '*'
          )
        } catch {
          console.log('no token')
        }
        break
      case 'reset-idle-timer':
        resetIdleTimer()
        break
      default: {
        if (data?.message === 'analytics_event') {
          if (data?.event?.eventName === 'submit') {
            dispatch(
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.botEvent,
                eventName: data?.event?.eventName,
                element: 'bot/' + data?.event?.data?.protocol_id,
                data: fortmatBotAnalyticsData(data?.event?.data),
              })
            )
          } else if (data?.event?.eventName === 'invalid') {
            dispatch(
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.botEvent,
                eventName: data?.event?.eventName,
                element: 'bot/' + data?.event?.data?.protocol_id,
                data: fortmatBotAnalyticsData(data?.event?.data),
                error: formatBotAnalyticsError(data?.event?.data?.error),
              })
            )
          } else if (data?.event?.eventName === 'click') {
            dispatch(
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.click,
                eventName: data?.event?.eventName,
                data: customClickTracking(
                  data?.event?.eventName,
                  data?.event?.data?.hitScope
                ),
              })
            )
          }
        }
        break
      }
    }
  }

  const formatBotAnalyticsError = error => {
    return {
      errorFields: [
        { name: ['Validation Error'], errors: [error], warnings: [] },
      ],
    }
  }

  // we need to add risk assesment bot if the patient did not answer it yet
  // we are adding it on hmo member or primary care (tele)
  const getBotTypes = () => {
    const RiskAssessmentBotType = Origin.RISK_ASSESSMENT

    if (isHmoMember) {
      const hmoSelectedBotTypes = getBotTypesByIntent(hmoSelectedIntent)
      if (!isCompleteHealthHistoryForm && !isMinor) {
        hmoSelectedBotTypes.push(RiskAssessmentBotType)
      }
      return hmoSelectedBotTypes
    } else if (selectedService === clinicTypes.PRIMARY) {
      return isCompleteHealthHistoryForm || isMinor
        ? [origin]
        : [origin, RiskAssessmentBotType]
    }

    return [origin]
  }

  const getUri = () => {
    const locale = getLocale()
    var id = member?.id || 123456

    const dob = member?.dateOfBirth
      ? moment(member.dateOfBirth).format(config.botDateFormat)
      : null

    const firstName = member?.firstName

    const uri = uriEncodeSearchParams(botURL(variant), {
      userId: id,
      userName: firstName,
      dob,
      gender: member?.sex ? member?.sex : userDetails?.sex,
      uVn: id,
      locale,
      variant: variant,
      origin: origin,
      htnOrigin: patientReferredToHtn ? 'Doctor' : 'App',
      patientOrigin: 'SOW',
      isHmoMember,
      botTypes: getBotTypes(),
    })
    return uri
  }
  const handleLoader = () => {
    setLoader(false)
  }

  return (
    <div className={'medical-bot'}>
      {loader ? <Spin className="bot-loader-centre" size="large" /> : null}
      {!isProduction && (
        <Button type="link" className="skip-btn" onClick={onBotEnded}>
          {$t('common.skip')}
        </Button>
      )}
      <iframe
        className={'bot-iframe-page'}
        name="bot-iframe-page"
        id="bot-iframe-page"
        onLoad={handleLoader}
        src={getUri()}
      />
    </div>
  )
}

export default Bot
